import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaQuoteLeft, FaStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './Testimonials.css';

const Testimonials = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      position: "CEO, TechStart Inc.",
      image: "https://ui-avatars.com/api/?name=Sarah+Johnson&background=667eea&color=fff&size=200",
      text: "Avionverse transformed our business operations completely. Their AI automation solutions reduced our processing time by 70% and improved accuracy significantly. Outstanding service!",
      rating: 5,
      company: "TechStart Inc."
    },
    {
      id: 2,
      name: "Michael Chen",
      position: "Operations Director, GlobalCorp",
      image: "https://ui-avatars.com/api/?name=Michael+Chen&background=764ba2&color=fff&size=200",
      text: "The team at Avionverse is exceptional. They understood our needs perfectly and delivered a custom AI solution that exceeded our expectations. Highly recommended!",
      rating: 5,
      company: "GlobalCorp"
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      position: "CTO, InnovateLabs",
      image: "https://ui-avatars.com/api/?name=Emily+Rodriguez&background=667eea&color=fff&size=200",
      text: "Working with Avionverse was a game-changer. Their intelligent automation platform helped us scale our operations while reducing costs by 45%. True professionals!",
      rating: 5,
      company: "InnovateLabs"
    },
    {
      id: 4,
      name: "David Thompson",
      position: "Founder, DataFlow Solutions",
      image: "https://ui-avatars.com/api/?name=David+Thompson&background=764ba2&color=fff&size=200",
      text: "Avionverse's AI chatbots and automation tools have revolutionized our customer service. Response times are faster, and customer satisfaction has increased by 60%.",
      rating: 5,
      company: "DataFlow Solutions"
    },
    {
      id: 5,
      name: "Lisa Anderson",
      position: "VP of Technology, FutureTech",
      image: "https://ui-avatars.com/api/?name=Lisa+Anderson&background=667eea&color=fff&size=200",
      text: "The predictive analytics solution from Avionverse gave us insights we never thought possible. Their expertise in AI is unmatched. Worth every penny!",
      rating: 5,
      company: "FutureTech"
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection) => {
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => {
      let nextIndex = prevIndex + newDirection;
      if (nextIndex < 0) nextIndex = testimonials.length - 1;
      if (nextIndex >= testimonials.length) nextIndex = 0;
      return nextIndex;
    });
  };

  // Auto-play slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(timer);
  }, [currentIndex]);

  return (
    <section id="testimonials" className="section testimonials" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: -50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2>What Our Clients Say</h2>
          <p>Don't just take our word for it - hear from our satisfied clients</p>
        </motion.div>

        <div className="testimonials-slider">
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              className="testimonial-card glass"
            >
              <div className="quote-icon">
                <FaQuoteLeft />
              </div>
              
              <div className="testimonial-content">
                <div className="rating">
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
                
                <p className="testimonial-text">
                  "{testimonials[currentIndex].text}"
                </p>
                
                <div className="client-info">
                  <img 
                    src={testimonials[currentIndex].image} 
                    alt={testimonials[currentIndex].name}
                    className="client-image"
                  />
                  <div className="client-details">
                    <h4>{testimonials[currentIndex].name}</h4>
                    <p className="client-position">{testimonials[currentIndex].position}</p>
                    <p className="client-company">{testimonials[currentIndex].company}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <button 
            className="slider-button prev"
            onClick={() => paginate(-1)}
            aria-label="Previous testimonial"
          >
            <FaChevronLeft />
          </button>
          
          <button 
            className="slider-button next"
            onClick={() => paginate(1)}
            aria-label="Next testimonial"
          >
            <FaChevronRight />
          </button>

          <div className="slider-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => {
                  setDirection(index > currentIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
